/** Single note in a synthesized notification melody. */
export interface MelodyNote {
  freq: number;
  start: number;
  dur: number;
  type?: OscillatorType;
  vol?: number;
}

/** Warm ascending fanfare (~2.3s) — loud, regal G-major arpeggio for new orders. */
export const VENDOR_NEW_ORDER_MELODY: MelodyNote[] = [
  { freq: 196, start: 0, dur: 0.55, type: 'sine', vol: 0.36 },
  { freq: 392, start: 0, dur: 0.16, type: 'triangle', vol: 0.46 },
  { freq: 493.88, start: 0.1, dur: 0.16, type: 'triangle', vol: 0.48 },
  { freq: 587.33, start: 0.2, dur: 0.18, type: 'sine', vol: 0.5 },
  { freq: 783.99, start: 0.34, dur: 0.2, type: 'sine', vol: 0.52 },
  { freq: 987.77, start: 0.5, dur: 0.24, type: 'sine', vol: 0.54 },
  { freq: 1174.66, start: 0.72, dur: 0.3, type: 'sine', vol: 0.56 },
  { freq: 196, start: 0.98, dur: 0.65, type: 'sine', vol: 0.38 },
  { freq: 1567.98, start: 0.98, dur: 0.5, type: 'sine', vol: 0.58 },
  { freq: 1318.51, start: 1.42, dur: 0.28, type: 'sine', vol: 0.5 },
  { freq: 987.77, start: 1.68, dur: 0.55, type: 'sine', vol: 0.52 },
];

/**
 * Loud alert triple-beep (~1.8s cycle) — clear kitchen-timer style (not a siren).
 * Loops until dismissed.
 */
export const VENDOR_ORDER_RING_CYCLE_SEC = 1.8;

export const VENDOR_ORDER_RING_MELODY: MelodyNote[] = [
  // Beep 1
  { freq: 880, start: 0, dur: 0.18, type: 'square', vol: 0.72 },
  { freq: 1760, start: 0, dur: 0.18, type: 'sine', vol: 0.38 },
  // Beep 2
  { freq: 880, start: 0.28, dur: 0.18, type: 'square', vol: 0.75 },
  { freq: 1760, start: 0.28, dur: 0.18, type: 'sine', vol: 0.4 },
  // Beep 3
  { freq: 988, start: 0.56, dur: 0.22, type: 'square', vol: 0.78 },
  { freq: 1976, start: 0.56, dur: 0.22, type: 'sine', vol: 0.42 },
  // Short pause, then a punchier pair
  { freq: 1046.5, start: 1.05, dur: 0.2, type: 'square', vol: 0.8 },
  { freq: 2093, start: 1.05, dur: 0.2, type: 'sine', vol: 0.44 },
  { freq: 1046.5, start: 1.35, dur: 0.22, type: 'square', vol: 0.8 },
  { freq: 2093, start: 1.35, dur: 0.22, type: 'sine', vol: 0.44 },
];

/** Crystal chime (~1.9s) — loud, elegant triple-bell reminder for pending orders. */
export const VENDOR_REMINDER_MELODY: MelodyNote[] = [
  { freq: 329.63, start: 0, dur: 0.4, type: 'triangle', vol: 0.32 },
  { freq: 659.25, start: 0, dur: 0.32, type: 'sine', vol: 0.52 },
  { freq: 783.99, start: 0.18, dur: 0.32, type: 'sine', vol: 0.54 },
  { freq: 987.77, start: 0.38, dur: 0.38, type: 'sine', vol: 0.56 },
  { freq: 1318.51, start: 0.62, dur: 0.42, type: 'triangle', vol: 0.58 },
  { freq: 329.63, start: 1.1, dur: 0.5, type: 'triangle', vol: 0.34 },
  { freq: 987.77, start: 1.1, dur: 0.28, type: 'sine', vol: 0.5 },
  { freq: 1174.66, start: 1.28, dur: 0.35, type: 'sine', vol: 0.54 },
  { freq: 1318.51, start: 1.5, dur: 0.45, type: 'sine', vol: 0.56 },
];

export function melodyDuration(notes: MelodyNote[]): number {
  return notes.reduce((max, n) => Math.max(max, n.start + n.dur), 0);
}

export function playMelody(ctx: AudioContext, notes: MelodyNote[]): void {
  const base = ctx.currentTime;
  for (const note of notes) {
    const start = base + note.start;
    const dur = note.dur;
    const vol = note.vol ?? 0.2;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = note.freq;
    osc.type = note.type ?? 'sine';
    gain.gain.setValueAtTime(0.001, start);
    gain.gain.linearRampToValueAtTime(vol, start + 0.012);
    gain.gain.setValueAtTime(vol * 0.85, start + dur * 0.3);
    gain.gain.exponentialRampToValueAtTime(0.001, start + dur);
    osc.start(start);
    osc.stop(start + dur + 0.04);
  }
}

function noteEnvelope(t: number, dur: number): number {
  const attack = 0.012;
  const release = Math.min(0.08, dur * 0.35);
  if (t < attack) {
    return t / attack;
  }
  if (t > dur - release) {
    const r = (dur - t) / release;
    return Math.max(0, r);
  }
  return 1;
}

function waveSample(phase: number, type: OscillatorType = 'sine'): number {
  if (type === 'triangle') {
    return 2 * Math.abs(2 * (phase - Math.floor(phase + 0.5))) - 1;
  }
  if (type === 'square') {
    return Math.sin(2 * Math.PI * phase) >= 0 ? 1 : -1;
  }
  if (type === 'sawtooth') {
    return 2 * (phase - Math.floor(phase + 0.5));
  }
  return Math.sin(2 * Math.PI * phase);
}

function sampleMelodyAt(t: number, notes: MelodyNote[]): number {
  let sum = 0;
  for (const note of notes) {
    const rel = t - note.start;
    if (rel < 0 || rel > note.dur) {
      continue;
    }
    const vol = note.vol ?? 0.2;
    const env = noteEnvelope(rel, note.dur);
    const phase = note.freq * rel;
    sum += waveSample(phase, note.type) * vol * env;
  }
  return Math.max(-1, Math.min(1, sum));
}

/** HTML5 Audio fallback when Web Audio is unavailable. */
/** Repeat a melody N times (for a longer Android notification WAV). */
export function repeatMelody(notes: MelodyNote[], repeats: number): MelodyNote[] {
  const cycle = melodyDuration(notes);
  const out: MelodyNote[] = [];
  for (let r = 0; r < repeats; r++) {
    const offset = r * cycle;
    for (const note of notes) {
      out.push({ ...note, start: note.start + offset });
    }
  }
  return out;
}

export function melodyToWavDataUrl(notes: MelodyNote[]): string {
  const sampleRate = 44100;
  const duration = melodyDuration(notes) + 0.05;
  const numSamples = Math.floor(sampleRate * duration);
  const numChannels = 1;
  const bitsPerSample = 16;
  const blockAlign = numChannels * (bitsPerSample / 8);
  const byteRate = sampleRate * blockAlign;
  const dataSize = numSamples * blockAlign;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);
  let offset = 0;
  const writeStr = (s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(offset++, s.charCodeAt(i));
  };
  writeStr('RIFF');
  view.setUint32(offset, 36 + dataSize, true);
  offset += 4;
  writeStr('WAVE');
  writeStr('fmt ');
  view.setUint32(offset, 16, true);
  offset += 4;
  view.setUint16(offset, 1, true);
  offset += 2;
  view.setUint16(offset, numChannels, true);
  offset += 2;
  view.setUint32(offset, sampleRate, true);
  offset += 4;
  view.setUint32(offset, byteRate, true);
  offset += 4;
  view.setUint16(offset, blockAlign, true);
  offset += 2;
  view.setUint16(offset, bitsPerSample, true);
  offset += 2;
  writeStr('data');
  view.setUint32(offset, dataSize, true);
  offset += 4;
  for (let i = 0; i < numSamples; i++) {
    const t = i / sampleRate;
    const sample = sampleMelodyAt(t, notes) * 32767 * 1.0;
    view.setInt16(offset, Math.max(-32768, Math.min(32767, sample)), true);
    offset += 2;
  }
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return 'data:audio/wav;base64,' + btoa(binary);
}
